import { memo, useMemo } from 'react';

import { gemPresentationForTier, type CalendarDayGemTier } from '../../utils/calendarGemDays';
import { generateGemSpec, type GemIntensity, type GemVariant } from '../../utils/gemGenerator';
import { GemSvgView } from './GemSvgView';

type CalendarGemBadgeProps = {
  seed: string;
  tier?: CalendarDayGemTier;
  variant?: GemVariant;
  size?: number;
  enableScan?: boolean;
  intensity?: GemIntensity;
  className?: string;
};

function hashDelay(seed: string) {
  let hash = 0;
  for (let index = 0; index < seed.length; index += 1) {
    hash = (hash * 31 + seed.charCodeAt(index)) >>> 0;
  }
  return hash;
}

export const CalendarGemBadge = memo(function CalendarGemBadge({
  seed,
  tier,
  variant,
  size = 22,
  enableScan,
  intensity,
  className = '',
}: CalendarGemBadgeProps) {
  const presentation = tier ? gemPresentationForTier(tier) : null;
  const resolvedVariant = presentation?.variant ?? variant ?? 'review';
  const resolvedEnableScan = presentation?.enableScan ?? enableScan ?? resolvedVariant === 'complete';
  const resolvedIntensity = presentation?.intensity ?? intensity ?? 'normal';
  const scanDelayMs = (resolvedIntensity === 'enhanced' ? 650 : 900) + (hashDelay(seed) % 700);

  const spec = useMemo(
    () => generateGemSpec(seed, resolvedVariant, size, resolvedIntensity),
    [resolvedIntensity, resolvedVariant, seed, size],
  );

  return (
    <span
      className={`relative inline-flex shrink-0 items-center justify-center ${className}`}
      style={{ width: size, height: size }}
      aria-hidden="true"
    >
      {resolvedVariant === 'complete' ? (
        <span
          className="absolute -z-10 rounded-full"
          style={{
            width: size * (resolvedIntensity === 'enhanced' ? 1.12 : 1.05),
            height: size * (resolvedIntensity === 'enhanced' ? 1.12 : 1.05),
            backgroundColor: spec.palette.glow,
            opacity: resolvedIntensity === 'enhanced' ? 0.62 : 0.45,
          }}
        />
      ) : null}

      <GemSvgView seed={seed} variant={resolvedVariant} size={size} intensity={resolvedIntensity} />

      {resolvedEnableScan && resolvedVariant === 'complete' ? (
        <span className="pointer-events-none absolute inset-0 overflow-hidden rounded-full">
          <span
            className={`absolute left-1/2 top-1/2 rounded-full ${
              resolvedIntensity === 'enhanced' ? 'gem-scan-beam-enhanced' : 'gem-scan-beam'
            }`}
            style={{
              width: size * (resolvedIntensity === 'enhanced' ? 0.38 : 0.34),
              height: size * 1.35,
              animationDelay: `${scanDelayMs}ms`,
            }}
          />
        </span>
      ) : null}
    </span>
  );
});
